import { GameMediaUrls } from '../GameMediaUrls';
import { Conditions, WinCondition } from '../WinCondition';
import { GemProps } from './Gems';
import { IStory } from '@storyverseco/svs-story-suite';
import { CraftRecipe } from './CraftRecipe';

export interface GameCreateProps {
  story?: IStory;
  gemData?: GemProps;
  correctChoices?: Conditions;
  free?: boolean;
}

export type GemType = 'coin' | 'diamond' | 'craftable'

export interface GameSchema {
  id: number;
  created_at: string;
  creator_address: string;
  game_id: number;
  game_path: string;
  charpass_contract_address: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  multimedia_urls: Record<string, any>;
  tx_hash: string;
  gem_name?: string;
  gem_image?: string;
  win_condition?:  WinCondition;
  skip_feed?: boolean;
  gem_type?: GemType;
  genres?: string[];
  tags?: string[];
  is_remix?: boolean;
}

export interface ExtraInfo {
  gemBuyPrice: number;
  canSell: boolean;
  gemSellPrice: number;
  gemsCount: number;
  holdersCount: number;
  holdersImage: string[];
  creatorPortfolioWei: number;
  liked: boolean;
  likeCount: number;
  commentCount: number;
  outcome?: {
    state: 'locked' | 'unlocked';
    url: string;
  },
  recipe?: CraftRecipe,
  recipeProgress?: string
}

/**
 * @deprecated Use `GameMediaUrls`
 */
export type GameMultimediaUrls = GameMediaUrls;

export interface GameFeedItem {
  id: number;
  createdAt: string;
  creatorAddress: string;
  gameId: number;
  gamePath: string;
  charpassContractAddress: string;
  multimediaUrls: GameMediaUrls;
  txHash: string;
  gemName: string;
  gemImage?: string; // @TODO: this should not be optional, but for now we dont have gem images
  winCondition:  WinCondition;
  twitter?: TwitterFeedInfo
  freeGame: boolean;
  gemType?: GemType;
  genres?: string[];
  tags?: string[];
  isRemix?: boolean;
  /**
   * @Note For internal use only
   */
  readonly onChainGameId: number;
}

export const getGameFeedItemFromSchema = (schema: GameSchema, twitter?: TwitterFeedInfo): GameFeedItem => {
  if (!schema.win_condition) {
    throw new Error(`Error (getGameFeedItemFromSchema): 'win_condition' is required for a game to be in the feed`);
  }
  return {
    id: schema.id, // offchain id
    createdAt: schema.created_at,
    creatorAddress: schema.creator_address,
    gameId: schema.id, // offchain id
    gamePath: schema.game_path,
    charpassContractAddress: schema.charpass_contract_address,
    multimediaUrls: schema.multimedia_urls as GameMediaUrls,
    txHash: schema.tx_hash,
    gemName: schema.gem_name || '',
    gemImage: schema.gem_image,
    winCondition: schema.win_condition,
    twitter,
    freeGame: Boolean(schema.skip_feed),
    onChainGameId: schema.game_id, // used to map back into game schema onchain id
    gemType: schema.gem_type,
    genres: schema.genres,
    tags: schema.tags,
    isRemix: schema.is_remix
  }
}

interface TwitterFeedInfo {
  handle: string;
  image: string;
  displayName: string;
}

export const getSchemaFromGameFeedItem = (feedItem: GameFeedItem): GameSchema => {
  return {
    id: feedItem.gameId, // offchain id
    created_at: feedItem.createdAt,
    creator_address: feedItem.creatorAddress,
    game_id: feedItem.onChainGameId, // onchain id
    game_path: feedItem.gamePath,
    charpass_contract_address: feedItem.charpassContractAddress,
    multimedia_urls: feedItem.multimediaUrls,
    tx_hash: feedItem.txHash,
    gem_name: feedItem.gemName,
    gem_image: feedItem.gemImage,
    win_condition: feedItem.winCondition,
    skip_feed: feedItem.freeGame,
    genres: feedItem.genres,
    tags: feedItem.tags,
    is_remix: feedItem.isRemix
  }
}
